/**
 * Currency Formatting Module
 * 
 * Centralized currency formatting using Intl.NumberFormat API.
 * Ensures consistent formatting across UI and PDF output.
 * 
 * Features:
 * - Locale-aware formatting for all supported currencies
 * - Proper symbol placement and thousand separators
 * - Correct decimal precision per currency
 * - Unicode symbol support with fallbacks
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Map of currency codes to their preferred locales
 * Each locale provides region-specific formatting preferences
 */
export const CURRENCY_LOCALES = Object.freeze({
  USD: 'en-US',      // $1,234.56
  EUR: 'de-DE',      // 1.234,56 €
  GBP: 'en-GB',      // £1,234.56
  INR: 'en-IN',      // ₹1,23,456.00 (Indian numbering)
  JPY: 'ja-JP',      // ¥1,234 (no decimals)
  AUD: 'en-AU',      // A$1,234.56
  CAD: 'en-CA',      // C$1,234.56
  CHF: 'fr-CH',      // 1'234.56 CHF (Swiss format)
  CNY: 'zh-CN',      // ¥1,234.56
  SEK: 'sv-SE',      // 1.234,56 kr
  NZD: 'en-NZ',      // NZ$1,234.56
  SGD: 'en-SG',      // S$1,234.56
  AED: 'ar-AE',      // AED 1,234.56
  SAR: 'ar-SA',      // SAR 1,234.56
  PHP: 'en-PH',      // ₱1,234.56
  PKR: 'en-PK',      // ₨1,234.56
  BDT: 'en-BD',      // ৳1,234.56
  NGN: 'en-NG',      // ₦1,234.56
  KES: 'en-KE',      // KSh1,234.56
});

/**
 * Currency symbol metadata with Unicode and fallback values
 */
export const CURRENCY_SYMBOLS = Object.freeze({
  USD: { symbol: '$', unicode: '$', fallback: 'USD' },
  EUR: { symbol: '€', unicode: '\u20AC', fallback: 'EUR' },
  GBP: { symbol: '£', unicode: '\u00A3', fallback: 'GBP' },
  INR: { symbol: '₹', unicode: '\u20B9', fallback: 'INR' },
  JPY: { symbol: '¥', unicode: '\u00A5', fallback: 'JPY' },
  AUD: { symbol: 'A$', unicode: 'A$', fallback: 'AUD' },
  CAD: { symbol: 'C$', unicode: 'C$', fallback: 'CAD' },
  CHF: { symbol: 'CHF', unicode: 'CHF', fallback: 'CHF' },
  CNY: { symbol: '¥', unicode: '\u00A5', fallback: 'CNY' },
  SEK: { symbol: 'kr', unicode: 'kr', fallback: 'SEK' },
  NZD: { symbol: 'NZ$', unicode: 'NZ$', fallback: 'NZD' },
  SGD: { symbol: 'S$', unicode: 'S$', fallback: 'SGD' },
  AED: { symbol: 'AED', unicode: 'AED', fallback: 'AED' },
  SAR: { symbol: 'SAR', unicode: 'SAR', fallback: 'SAR' },
  PHP: { symbol: '₱', unicode: '₱', fallback: 'PHP' },
  PKR: { symbol: '₨', unicode: '₨', fallback: 'PKR' },
  BDT: { symbol: '৳', unicode: '৳', fallback: 'BDT' },
  NGN: { symbol: '₦', unicode: '₦', fallback: 'NGN' },
  KES: { symbol: 'KSh', unicode: 'KSh', fallback: 'KES' },
});

/**
 * Currencies that don't use decimal places
 */
export const ZERO_DECIMAL_CURRENCIES = Object.freeze(['JPY']);

/**
 * Default currency code
 */
export const DEFAULT_CURRENCY = 'USD';

// ============================================================================
// FORMATTER CACHE
// ============================================================================

/**
 * Cache for Intl.NumberFormat instances to improve performance
 */
const formatterCache = new Map();

/**
 * Get or create a cached Intl.NumberFormat instance
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {Intl.NumberFormat} Cached formatter instance
 */
function getFormatter(currencyCode) {
  if (formatterCache.has(currencyCode)) {
    return formatterCache.get(currencyCode);
  }
  
  const locale = CURRENCY_LOCALES[currencyCode] || CURRENCY_LOCALES[DEFAULT_CURRENCY];
  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.includes(currencyCode);
  
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: isZeroDecimal ? 0 : 2,
    maximumFractionDigits: isZeroDecimal ? 0 : 2,
  });
  
  formatterCache.set(currencyCode, formatter);
  return formatter;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if a currency code is supported
 * @param {string} currencyCode - Currency code to check
 * @returns {boolean} Whether the currency is supported
 */
export function isSupportedCurrency(currencyCode) {
  return Object.prototype.hasOwnProperty.call(CURRENCY_LOCALES, currencyCode);
}

/**
 * Get the safe currency code (defaults to USD if unsupported)
 * @param {string} currencyCode - Currency code to validate
 * @returns {string} Valid currency code
 */
export function getSafeCurrencyCode(currencyCode) {
  return isSupportedCurrency(currencyCode) ? currencyCode : DEFAULT_CURRENCY;
}

/**
 * Get decimal places for a currency
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {number} Number of decimal places (0 or 2)
 */
export function getDecimalPlaces(currencyCode) {
  return ZERO_DECIMAL_CURRENCIES.includes(currencyCode) ? 0 : 2;
}

// ============================================================================
// MAIN FORMATTING FUNCTIONS
// ============================================================================

/**
 * Format a number as currency using Intl.NumberFormat
 * 
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @returns {string} Formatted currency string with symbol
 * 
 * @example
 * formatCurrency(1234.56, 'USD') // "$1,234.56"
 * formatCurrency(1234.56, 'EUR') // "1.234,56 €"
 * formatCurrency(1234, 'JPY')    // "¥1,234"
 */
export function formatCurrency(amount, currencyCode = DEFAULT_CURRENCY) {
  // Handle invalid inputs
  if (amount === null || amount === undefined || isNaN(amount)) {
    amount = 0;
  }
  
  const safeCode = getSafeCurrencyCode(currencyCode);
  
  try {
    const formatter = getFormatter(safeCode);
    let formatted = formatter.format(amount);
    
    // Ensure proper Unicode encoding for special symbols
    const symbolInfo = CURRENCY_SYMBOLS[safeCode];
    if (symbolInfo && (safeCode === 'INR' || safeCode === 'JPY' || safeCode === 'CNY')) {
      // Replace potentially problematic characters with proper Unicode
      formatted = formatted.replace(/₹|¥/g, symbolInfo.unicode);
    }
    
    return formatted;
  } catch (error) {
    // Fallback formatting if Intl fails
    console.warn(`Currency formatting error for ${currencyCode}:`, error);
    const decimals = getDecimalPlaces(safeCode);
    const symbol = CURRENCY_SYMBOLS[safeCode]?.fallback || safeCode;
    return `${symbol} ${Number(amount).toFixed(decimals)}`;
  }
}

/**
 * Get just the currency symbol for a currency code
 * 
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {boolean} useFallback - Return text fallback if symbol might not render
 * @returns {string} Currency symbol or fallback text
 * 
 * @example
 * getCurrencySymbol('USD') // "$"
 * getCurrencySymbol('INR') // "₹"
 * getCurrencySymbol('INR', true) // "INR" (for PDF/contexts where Unicode might fail)
 */
export function getCurrencySymbol(currencyCode = DEFAULT_CURRENCY, useFallback = false) {
  const safeCode = getSafeCurrencyCode(currencyCode);
  const symbolInfo = CURRENCY_SYMBOLS[safeCode];
  
  if (!symbolInfo) {
    return '$';
  }
  
  // For multi-character text symbols, always use as-is
  if (symbolInfo.symbol.length > 1) {
    return useFallback ? symbolInfo.fallback : symbolInfo.symbol;
  }
  
  // For single-character symbols, use Unicode version
  return useFallback ? symbolInfo.fallback : symbolInfo.unicode;
}

/**
 * Format amount with optional symbol
 * Useful when you need just the formatted number without full currency formatting
 * 
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {boolean} includeSymbol - Whether to include the symbol
 * @returns {string} Formatted string
 * 
 * @example
 * formatAmount(1234.56, 'USD', true)  // "$1,234.56"
 * formatAmount(1234.56, 'USD', false) // "1234.56"
 */
export function formatAmount(amount, currencyCode = DEFAULT_CURRENCY, includeSymbol = true) {
  if (includeSymbol) {
    return formatCurrency(amount, currencyCode);
  }
  
  const decimals = getDecimalPlaces(currencyCode);
  return (parseFloat(amount) || 0).toFixed(decimals);
}

/**
 * Parse a formatted currency string back to a number
 * 
 * @param {string} formattedValue - Formatted currency string
 * @param {string} currencyCode - Currency code for context
 * @returns {number} Parsed numeric value
 * 
 * @example
 * parseCurrencyValue("$1,234.56", 'USD') // 1234.56
 * parseCurrencyValue("1.234,56 €", 'EUR') // 1234.56
 */
export function parseCurrencyValue(formattedValue, currencyCode = DEFAULT_CURRENCY) {
  if (typeof formattedValue === 'number') {
    return formattedValue;
  }
  
  if (!formattedValue || typeof formattedValue !== 'string') {
    return 0;
  }
  
  // Remove all non-numeric characters except decimal separators
  let cleaned = formattedValue.replace(/[^\d.,-]/g, '');
  
  // Determine which is the decimal separator based on locale
  const locale = CURRENCY_LOCALES[currencyCode] || 'en-US';
  const usesCommaDecimal = ['de-DE', 'fr-CH', 'sv-SE'].some(l => locale.includes(l));
  
  if (usesCommaDecimal) {
    // European format: 1.234,56 -> 1234.56
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // US/UK format: 1,234.56 -> 1234.56
    cleaned = cleaned.replace(/,/g, '');
  }
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Get display-friendly currency options for select dropdowns
 * @returns {Array<{code: string, symbol: string, label: string}>}
 */
export function getCurrencyOptions() {
  return Object.entries(CURRENCY_SYMBOLS).map(([code, info]) => ({
    code,
    symbol: info.symbol,
    label: `${code} (${info.symbol})`,
  }));
}

// ============================================================================
// EXPORTS
// ============================================================================

const currencyFormatterExports = {
  formatCurrency,
  getCurrencySymbol,
  formatAmount,
  parseCurrencyValue,
  getCurrencyOptions,
  isSupportedCurrency,
  getSafeCurrencyCode,
  getDecimalPlaces,
  CURRENCY_LOCALES,
  CURRENCY_SYMBOLS,
  ZERO_DECIMAL_CURRENCIES,
  DEFAULT_CURRENCY,
};

export default currencyFormatterExports;
